/*
 * Copyright (c) 2022, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {forwardRef, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {
    PRODUCT_TILE_IMAGE_TYPE,
    TILE_CONTEXT_ACCESSORIES,
    TILE_CONTEXT_FANREEL_RELATED_PRODUCTS
} from '../../constants'

// Components
import {AspectRatio, Box, Flex, Text, useMultiStyleConfig} from '@chakra-ui/react'

import DynamicImage from '../dynamic-image'
import SwatchGroup from '../swatch-group'
import Swatch from '../swatch-group/swatch'
import {trackFanreelClickProduct, trackProductTileClick} from '../../tracking/analytics'
import BadgeGroup from '../badge-group'
import Link from '../link'
import {ProductQuickviewModal, useProductQuickviewModal} from '../product-quickview-modal'

// Hooks
import {FormattedMessage, useIntl} from 'react-intl'

// Other
import {productUrlBuilder} from '../../utils/url'
import {useColorVariation} from '../../hooks'
import {useProductDisplayImages} from '../../hooks/use-product-display-images'
import {
    getPreferedVariant,
    isProductMaster,
    isProductSet,
    isProductVariant
} from '../../utils/product-utils'
import {useProductSetPrice} from '../../hooks/useScapiCustomApi'
import {isServer} from '../../utils/utils'
import AddToCartCTA from '../add-to-cart-cta'
import CustomizeCTA from './partials/customize-cta'
import RemoveFromWishlistOverlayButton from './partials/remove-from-wishlist-overlay-button'
import WishlistOverlayButton from './partials/wishlist-overlay-button'
import {ProductTileStyleProvider} from './context'
import {useProduct} from '@salesforce/commerce-sdk-react'
import {DEFAULT_GET_PRODUCT_PRICE_PARAMETERS} from '../../hooks/useProductDetail'
import useEffectEvent from '../../hooks/use-effect-event'
import {CloseIcon, UnavailableCartIcon} from '../icons'
import ProductDisplayPrice from '../product-display-price'
import ColorSwatch from '../icons/colorSwatch'

export {default as Skeleton} from './partials/skeleton'

/**
 * The ProductTile is a visual representation of a product object. Default handling is:
 * * click on tile opens QuickViewModal with tracking when `context` is
 *   `TILE_CONTEXT_ACCESSORIES` or `TILE_CONTEXT_FANREEL_RELATED_PRODUCTS`
 * * hover on tile shows second image if available
 * * click on color variation changes the displayed product variant (only for `default` and `reco` variants)
 * * click on wishlist overlay button adds/removes product from wishlist (only for `basic`, `default`, `reco` and `wishlist` variants)
 * * click on add to cart cta button adds product to cart (only for `wishlist` variant)
 *
 * @param {string} props.context - context where the product tile is used - used for tracking.
 *   Do not extend the list of contexts - rather use `onClick` handler
 * @param {object} props.dynamicImageProps - `imageProps` for DynamicImage component
 * @param {object} props.product - the product data that will be represented in this component
 * @param {boolean} props.propagateDisabled - true to disable selecting unorderable variants
 * @param {'basic'|'default'|'order'|'reco'|'wishlist'} props.variant - the variant of the product tile:
 * - `basic`: images, product name, tags and add/remove from wishlist overlay button
 * - `default`: images, product name, product price, color variations and add/remove from wishlist overlay button
 * - `order`: image and product name
 * - `reco`: images, product name, product price, color variations and add/remove from wishlist overlay button
 * - `wishlist`: images, product name, product price, current color variation, remove from wishlist overlay button
 *   and add to cart cta button
 * @param {function(product: object, event: object): boolean|void} props.onClick - click on tile. If it returns false,
 *   the default behavior is prevented
 * @param {function(product: object, event: object): boolean|void} props.onRecommendedProductClick -
 *   click on recommended product from add to cart modal
 *
 */
const ProductTile = forwardRef((props, ref) => {
    const {formatMessage, locale} = useIntl()
    const styles = useMultiStyleConfig('ProductTile', props)
    const finalFocusRef = useRef(null)

    const {
        context,
        dynamicImageProps,
        isDisabled,
        product: initialProduct,
        propagateDisabled,
        titleProps,
        variant = 'default',
        onClick: _onClick,
        onRecommendedProductClick: _onRecommendedProductClick,
        onOverlayCtaClick,
        ...rest
    } = props

    const quickViewModalProps = useProductQuickviewModal()
    const onRecommendedProductClick = _onRecommendedProductClick || quickViewModalProps.onOpen
    quickViewModalProps.onRecommendedProductClick = onRecommendedProductClick

    // this is an internal attribute added by wishlist and order details when the product does not exist
    const isProductDiscontinued = initialProduct?.productExists === false
    const isProductActive =
        !(isProductMaster(initialProduct) || isProductVariant(initialProduct)) ||
        initialProduct?.variants?.length > 0

    // selected product id
    const [_selectedProductId, setSelectedProductId] = useState(null)
    // in case the initial product changes we might have an old selected product id
    const selectedProductId = useMemo(() => {
        if (
            _selectedProductId &&
            initialProduct?.variants?.find((variant) => variant.productId === _selectedProductId)
        ) {
            return _selectedProductId
        }

        if (isProductVariant(initialProduct)) {
            return initialProduct.id
        }
        if (isProductMaster(initialProduct)) {
            return getPreferedVariant(initialProduct)?.productId
        }
        return initialProduct.id
    }, [initialProduct, _selectedProductId])
    // clear selected product id if the initial product changes
    useEffect(() => {
        setSelectedProductId(null)
    }, [initialProduct])

    // computed product variant or the initial product if no variants
    const variantProduct = useMemo(() => {
        if (selectedProductId === initialProduct.id) {
            return initialProduct
        }
        const variant = initialProduct.variants?.find(
            (variant) => variant.productId === selectedProductId
        )
        if (!variant) {
            return initialProduct
        }
        const {productId, ...rest} = variant
        return {
            ...initialProduct,
            ...rest,
            name: initialProduct.name,
            id: productId
        }
    }, [initialProduct, selectedProductId])

    const {
        CTAAction,
        ctaActionProps,
        OverlayAction,
        showBadges,
        showPrice,
        showVariations,
        showCustomizable
    } = useMemo(
        () =>
            ({
                basic: {
                    OverlayAction: isProductActive ? WishlistOverlayButton : null,
                    showBadges: !isProductDiscontinued,
                    showPrice: !isProductDiscontinued
                },
                cro61: {
                    OverlayAction: isProductActive ? WishlistOverlayButton : null,
                    showBadges: !isProductDiscontinued,
                    showPrice: !isProductDiscontinued,
                    showVariations: isProductActive && !isProductDiscontinued,
                    showCustomizable: isProductSet(initialProduct)
                },
                default: {
                    OverlayAction: isProductActive ? WishlistOverlayButton : null,
                    showBadges: !isProductDiscontinued,
                    showPrice: !isProductDiscontinued,
                    showVariations: isProductActive && !isProductDiscontinued,
                    showCustomizable: isProductSet(initialProduct)
                },
                order: {},
                reco: {
                    OverlayAction: isProductActive ? WishlistOverlayButton : null,
                    showBadges: !isProductDiscontinued,
                    showPrice: !isProductDiscontinued,
                    showVariations: isProductActive && !isProductDiscontinued,
                    showCustomizable: isProductSet(initialProduct)
                },
                wishlist: {
                    ...(!isProductActive || isProductDiscontinued
                        ? null
                        : isProductSet(initialProduct)
                        ? {
                              CTAAction: CustomizeCTA,
                              ctaActionProps: {
                                  isDisabled
                              }
                          }
                        : {
                              CTAAction: AddToCartCTA,
                              ctaActionProps: {
                                  isDisabled,
                                  onClick: (e) => {
                                      e?.preventDefault?.()
                                      e?.stopPropagation?.()
                                  },
                                  onRecommendedProductClick: onRecommendedProductClick
                              }
                          }),
                    OverlayAction: RemoveFromWishlistOverlayButton,
                    showPrice: !isProductDiscontinued,
                    showVariations: isProductActive && !isProductDiscontinued
                }
            }[variant] || {}),
        [
            initialProduct,
            isDisabled,
            isProductActive,
            isProductDiscontinued,
            variant,
            onRecommendedProductClick
        ]
    )

    // load variant promotions
    // variant promotions do not appear when loading master product set with getProduct, so we need to fetch it for the selected variant
    const needsFetchingVariantPromotions =
        isProductMaster(initialProduct) && Boolean(selectedProductId)
    const {data: variantPromotionsData, isInitialLoading: isVariantPromotionsLoading} = useProduct(
        {
            parameters: {
                ...DEFAULT_GET_PRODUCT_PRICE_PARAMETERS,
                id: selectedProductId
            }
        },
        {
            // fetch only on client to include customer prices
            enabled: needsFetchingVariantPromotions && !isServer
        }
    )

    // load set prices
    // promotions do not appear when loading product set with getProduct, so we need to fetch it in any case
    const needsFetchingCustomerPrice = Boolean(initialProduct?.id) && isProductSet(initialProduct)
    const {data: productSetPricesData} = useProductSetPrice(
        {
            parameters: {
                locale,
                c_pid: initialProduct?.id
            }
        },
        {
            // fetch only on client to include customer prices
            enabled: needsFetchingCustomerPrice && !isServer
        }
    )
    const isPriceLoading =
        (needsFetchingCustomerPrice && !productSetPricesData?.resp) ||
        (needsFetchingVariantPromotions && isVariantPromotionsLoading)

    const productWithPrice = useMemo(() => {
        if (needsFetchingCustomerPrice) {
            return {
                ...variantProduct,
                ...productSetPricesData?.resp
            }
        }
        if (needsFetchingVariantPromotions) {
            return {
                ...variantProduct,
                ...variantPromotionsData
            }
        }
        return variantProduct
    }, [
        needsFetchingCustomerPrice,
        needsFetchingVariantPromotions,
        productSetPricesData,
        variantProduct,
        variantPromotionsData
    ])

    /**
     * Filter [imageGroups] by PRODUCT_TILE_IMAGE_TYPE and selected color variation.
     * Slice them because we need only first and second image
     */
    const images = useProductDisplayImages(
        productWithPrice,
        PRODUCT_TILE_IMAGE_TYPE,
        productWithPrice?.variationValues,
        2
    )

    /* Get extended variation attributes data with images etc. */
    const colorVariations = useColorVariation(productWithPrice)

    const changeColorVariation = (value) => {
        const variant = productWithPrice?.variants?.find(
            (variant) => variant.variationValues.color === value
        )

        if (variant?.productId) {
            setSelectedProductId(variant.productId)
        }
    }

    const onClick = (e) => {
        if (isDisabled) {
            e.preventDefault()
            return
        }

        if (_onClick?.(productWithPrice, e) === false) {
            return
        }

        switch (context) {
            case TILE_CONTEXT_ACCESSORIES:
                trackProductTileClick(context, productWithPrice.id)
                break
            case TILE_CONTEXT_FANREEL_RELATED_PRODUCTS:
                trackFanreelClickProduct(productWithPrice.id)
                break
            default:
                break
        }

        if (isProductSet(productWithPrice)) {
            // leave the default behavior for product sets
            return
        }

        e.preventDefault()
        finalFocusRef.current = e.currentTarget
        if (onRecommendedProductClick) {
            onRecommendedProductClick(productWithPrice.id, context, {
                finalFocusRef
            })
        } else {
            quickViewModalProps.onOpen(productWithPrice.id, context, {
                finalFocusRef
            })
        }
    }

    // This complex logic is needed to close the quick view modal after the product is added to the cart and add to cart modal is shown
    const [addedToCart, setAddedToCart] = useState(false)
    const onAfterAddToCart = useCallback(() => {
        setAddedToCart(true)
    }, [])
    const onAddedToCart = useEffectEvent(() => {
        quickViewModalProps.onClose()
    })
    useEffect(() => {
        if (addedToCart) {
            setAddedToCart(false)
            onAddedToCart()
        }
    }, [addedToCart, onAddedToCart])

    const {component: RootComponent, props: rootComponentProps} =
        isProductDiscontinued || initialProduct['c_product-type'] === 'Engraving'
            ? {
                  // Discontinued product or engraving
                  component: Box,
                  props: {
                      ...styles.container,
                      ...rest
                  }
              }
            : {
                  // Active product
                  component: Link,
                  props: {
                      variant: 'image',
                      cursor: 'pointer',
                      role: 'button',
                      tabIndex: '0',
                      ...styles.container,
                      to: productUrlBuilder(productWithPrice, locale),
                      'aria-disabled': isDisabled,
                      onClick,
                      ...rest,
                      ref
                  }
              }

    return (
        <ProductTileStyleProvider value={styles}>
            <Box
                position="relative"
                data-testid="product-tile"
                role="group"
                aria-label={formatMessage({
                    id: 'global.product',
                    defaultMessage: 'Product'
                })}
            >
                <RootComponent {...rootComponentProps}>
                    {/* Image */}
                    <AspectRatio ratio={7 / 8}>
                        <Box {...styles.imageContainer}>
                            {images.length > 0 ? (
                                <>
                                    <DynamicImage
                                        {...styles.image}
                                        {...(images.length > 1 && {
                                            'data-not-hovered': 'container'
                                        })}
                                        src={`${
                                            images[0].disBaseLink || images[0].link
                                        }[?sw={width}&q=60]`}
                                        widths={dynamicImageProps?.widths}
                                        imageProps={{
                                            alt: images[0].alt,
                                            loading: 'lazy',
                                            objectFit: 'contain',
                                            cursor: 'inherit',
                                            ...dynamicImageProps?.imageProps
                                        }}
                                    />
                                    {images.length > 1 && (
                                        <DynamicImage
                                            {...styles.image}
                                            data-hovered="container"
                                            src={`${
                                                images[1].disBaseLink || images[1].link
                                            }[?sw={width}&q=60]`}
                                            widths={dynamicImageProps?.widths}
                                            imageProps={{
                                                alt: images[1].alt,
                                                loading: 'lazy',
                                                objectFit: 'contain',
                                                cursor: 'inherit',
                                                ...dynamicImageProps?.imageProps
                                            }}
                                        />
                                    )}
                                    <Box
                                        data-not-hovered={images.length > 1 && 'container'}
                                        {...styles.imageOverlay}
                                    />
                                </>
                            ) : (
                                <Flex {...styles.unavailableImageContainer}>
                                    <Box {...styles.unavailableIconBox}>
                                        <UnavailableCartIcon boxSize={6} />
                                        <Box {...styles.closeIconRound}>
                                            <CloseIcon color={'white'} boxSize={1.5} />
                                        </Box>
                                    </Box>
                                    <Text {...styles.unavailableText}>
                                        <FormattedMessage
                                            defaultMessage="This product is no longer available."
                                            id="product_tile.discontinued"
                                        />
                                    </Text>
                                </Flex>
                            )}

                            {showBadges && !isPriceLoading && (
                                <BadgeGroup
                                    containerProps={styles.badgesContainer}
                                    product={productWithPrice}
                                    max={2}
                                    maxSummedChars={18}
                                />
                            )}
                        </Box>
                    </AspectRatio>

                    <Flex {...styles.infoContainer}>
                        <Flex {...styles.primaryInfoContainer}>
                            {/* Title */}
                            <Text {...styles.title} {...titleProps}>
                                {productWithPrice.c_masterName || productWithPrice.name}
                            </Text>

                            {/* Price */}
                            {showPrice && (
                                <ProductDisplayPrice
                                    {...styles.price}
                                    product={productWithPrice}
                                    loading={isPriceLoading}
                                    data-testid="product-tile-price"
                                />
                            )}
                        </Flex>
                    </Flex>
                    {/*customizable if its a set*/}
                    {showCustomizable && (
                        <Flex alignItems="center">
                            <ColorSwatch width="1.5rem" height="1.5rem" />
                            <Text
                                textStyle="Product Tile"
                                color="stokkePink.dark"
                                pl={1}
                                data-testid="producttile-customize"
                            >
                                <FormattedMessage
                                    defaultMessage="Customize"
                                    id="product_tile.customize"
                                />
                            </Text>
                        </Flex>
                    )}
                </RootComponent>

                <Box
                    width="100%"
                    position="absolute"
                    top={0}
                    aspectRatio="1/1.15"
                    pointerEvents="none"
                >
                    {OverlayAction && (
                        <OverlayAction
                            isDisabled={isDisabled}
                            product={productWithPrice}
                            onClick={onOverlayCtaClick}
                        />
                    )}
                </Box>

                {/* CTA Action */}
                {CTAAction && (
                    <CTAAction
                        {...styles.ctaAction}
                        product={productWithPrice}
                        {...ctaActionProps}
                    />
                )}

                {/* Color Variations */}
                {showVariations && colorVariations?.values?.length > 0 && (
                    <SwatchGroup
                        showLabel={false}
                        spacing={1}
                        size="sm"
                        {...styles.variations}
                        label={colorVariations.name}
                        value={productWithPrice?.variationValues?.color}
                        onChange={(value) => {
                            changeColorVariation(value)
                        }}
                    >
                        {colorVariations.values.map(({href, name, image, value, orderable}) => (
                            <Swatch
                                key={value}
                                href={href}
                                disabled={!orderable}
                                value={value}
                                name={
                                    orderable
                                        ? name
                                        : formatMessage(
                                              {
                                                  id: 'product_tile.variant_name.accessibility.out_of_stock',
                                                  defaultMessage: '{colorName} - out of stock'
                                              },
                                              {colorName: name}
                                          )
                                }
                                size="sm"
                                propagateDisabled={propagateDisabled}
                                tooltipPlacement="top"
                                {...styles.variationSwatch}
                            >
                                {image ? (
                                    <Box
                                        height="100%"
                                        width="100%"
                                        borderRadius="full"
                                        backgroundRepeat="no-repeat"
                                        backgroundSize="cover"
                                        backgroundColor={name.toLowerCase()}
                                        backgroundImage={
                                            image
                                                ? `url("${encodeURI(
                                                      image.disBaseLink || image.link
                                                  )}")`
                                                : ''
                                        }
                                        {...styles.variationImage}
                                    />
                                ) : (
                                    <Text {...styles.variationText}>{name.charAt(0)}</Text>
                                )}
                            </Swatch>
                        ))}
                    </SwatchGroup>
                )}
            </Box>

            <ProductQuickviewModal
                {...quickViewModalProps}
                onAfterAddToCart={onAfterAddToCart}
                showAddToCartModal={false}
                scrollToTop={false}
            />
        </ProductTileStyleProvider>
    )
})

ProductTile.displayName = 'ProductTile'

ProductTile.propTypes = {
    // tracking related context
    context: PropTypes.string,
    dynamicImageProps: PropTypes.object,
    isDisabled: PropTypes.bool,
    /**
     * The product data that will be represented in this component.
     */
    product: PropTypes.object,
    // true to disable selecting unorderable variants
    propagateDisabled: PropTypes.bool,
    // addition props for the title text
    titleProps: PropTypes.object,
    variant: PropTypes.string,
    // click on tile
    onClick: PropTypes.func,
    onCTAClick: PropTypes.func,
    onOverlayCtaClick: PropTypes.func,
    // click on recommended product on add to cart modal
    onRecommendedProductClick: PropTypes.func
}

export default ProductTile
